/**
 * MODULE: _shared/frontend-shell/src/layout/ShellRouteTransitionOverlay.tsx
 * PURPOSE: Full-viewport transition overlay when switching shell modules — BrowserRouter has no useNavigation loading state (#109)
 * DEPENDENCIES: react, react-dom, react-router-dom, flowbite-react, ./shellRouteContentKey, ../pages/MessagingShellSkeleton
 * PUBLIC_API: ShellRouteTransitionOverlay
 * SIDE_EFFECTS: DOM portal to document.body
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 74 / 150 lines
 * RECENT: 2026-05-22 [2.2.1132] — #109 messaging route flash fix; dismisses when messaging tablist mounts. [2.2.1132]
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Spinner } from 'flowbite-react';
import { shellRouteContentKey } from './shellRouteContentKey';
import { MessagingShellSkeleton } from '../pages/MessagingShellSkeleton';

const TRANSITION_MAX_MS = 1500;

export function ShellRouteTransitionOverlay() {
  const location = useLocation();
  const shellKey = shellRouteContentKey(location.pathname);
  const prevShellKeyRef = useRef(shellKey);
  const [active, setActive] = useState(false);

  useLayoutEffect(() => {
    if (shellKey !== prevShellKeyRef.current) {
      setActive(true);
      prevShellKeyRef.current = shellKey;
    }
  }, [shellKey]);

  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => setActive(false), TRANSITION_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [location.pathname, active]);

  useEffect(() => {
    if (!active || !location.pathname.startsWith('/messaging')) return;
    const dismissWhenReady = () => {
      const tablist = document.querySelector('[role="tablist"][aria-label*="Messaging" i]');
      if (tablist) setActive(false);
    };
    dismissWhenReady();
    const observer = new MutationObserver(dismissWhenReady);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [active, location.pathname]);

  if (!active || typeof document === 'undefined') {
    return null;
  }

  const messaging = location.pathname.startsWith('/messaging');

  return createPortal(
    <div
      className="fixed inset-x-0 bottom-0 top-14 z-[60] flex flex-col bg-gray-50 dark:bg-gray-900 md:top-[57px]"
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="shell-route-transition"
    >
      {messaging ? (
        <MessagingShellSkeleton />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <Spinner size="xl" aria-label="Loading page" />
        </div>
      )}
    </div>,
    document.body
  );
}
